import React, {
  useMemo,
  useState,
  useCallback,
  KeyboardEvent,
  useEffect,
  ReactNode,
} from "react";

import { createEditor, Descendant, Node } from "slate";
import { Slate, Editable, withReact, RenderLeafProps } from "slate-react";
import { withHistory } from "slate-history";

import { withBlocks } from "./plugins/with-blocks.plugin";
import { withFormats } from "./plugins/with-formats.plugin";
import { withHeaders } from "./plugins/with-headers.plugin";
import { withImages } from "./plugins/with-images.plugin";
import { withLinks } from "./plugins/with-link.plugin";
import { withPaste } from "./plugins/with-paste.plugin";
import {
  CustomEditor,
  CustomRenderElementProps,
  LinkElement as LinkElementType,
  ImageElement as ImageElementType,
} from "./slate";
import {
  clearBlock,
  isRemovableBlock,
  insertSoftBreak,
} from "./commands/q-editor.controls";
import Toolbar from "./components/toolbar.component";
import ImagePrompt, { ImageSizeUrl } from "./components/image-prompt.component";

import Leaf from "./leaves/leaf";
import {
  CodeBlockElement,
  BlockQuoteElement,
  DefaultElement,
  Header1Element,
  Header2Element,
  Header3Element,
  ListItemElement,
  OrderedListElement,
  UnorderedListElement,
  LinkElement,
  HorizontalLineElement,
  ImageElement,
} from "./elements";

export interface QEditorProps {
  customClassName?: string;
  layout: "default" | "inline" | "read-only";
  value?: Descendant[];
  setValue?: (value: Descendant[]) => void;
  placeholder?: string;
  imageRole?: string;
  readOnly?: boolean;
  children?: ReactNode;
  uploadUrl?: string;
  accessToken?: string | null;
}

export type EditorDescendant = Descendant;

export const serializeToText = (nodes: Descendant[] | null): string => {
  if (!nodes) {
    return "";
  }
  return nodes.map((node) => Node.string(node)).join("\n");
};

export const QEditor = ({
  layout = "default",
  value,
  setValue = (value: Descendant[]) => {},
  placeholder,
  imageRole = "",
  customClassName,
  readOnly = false,
  children,
  uploadUrl,
  accessToken,
}: QEditorProps) => {
  const editor = useMemo(
    () =>
      withPaste(
        withImages(
          withHeaders(
            withLinks(
              withFormats(
                withBlocks(
                  withHistory(withReact(createEditor() as CustomEditor))
                )
              )
            )
          )
        )
      ),
    []
  );
  const { insertText } = editor;

  editor.insertText = (text: string) => {
    if (!editor.operations.length) {
      insertText(text);
    }
  };

  const [isAdVisible, setIsAdVisible] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const initialValue: Descendant[] = useMemo(
    () => [
      {
        type: "paragraph",
        children: [{ text: placeholder || "" }],
      },
    ],
    [placeholder]
  );
  // #region handlers
  const handleOnKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const isMac = navigator?.userAgent?.toLowerCase().includes("mac os");

    if (event.key === "Backspace") {
      if (isRemovableBlock(editor)) {
        clearBlock(editor);
      }
    }

    if (event.shiftKey) {
      if (event.key === "Enter") {
        event.preventDefault();
        return insertSoftBreak(editor);
      }
    }

    if (event.ctrlKey) {
      if (event.altKey) {
        switch (event.key) {
          case "1":
            event.preventDefault();
            if (editor.isHeader1()) {
              editor.toggleOrderedHeader1();
            } else if (editor.isOrderedHeader1()) {
              editor.toggleOrderedHeader1();
            } else {
              editor.toggleHeader1();
            }
            return;
          case "2":
            event.preventDefault();
            editor.toggleHeader2();
            return;
          case "3":
            event.preventDefault();
            editor.toggleHeader3();
            return;
        }
      }
    }

    if ((isMac && event.metaKey) || (!isMac && event.ctrlKey)) {
      if (event.shiftKey) {
        switch (event.key) {
          case "z":
          case "Z":
            event.preventDefault();
            editor.redo();
            return;
        }
      }

      switch (event.key) {
        case "z":
          event.preventDefault();
          editor.undo();
          return;
        case "b":
          event.preventDefault();
          editor.toggleBold();
          return;
        case "i":
          event.preventDefault();
          editor.toggleItalic();
          return;
        case "u":
          event.preventDefault();
          editor.toggleUnderline();
          return;
        case "`":
          event.preventDefault();
          editor.toggleCodeBlock();
          return;
        case "k":
          event.preventDefault();
          editor.toggleLink();
      }
    }
  };

  const handleOnUpload = useCallback(
    (urls: ImageSizeUrl) => {
      editor.insertImage(urls);
      setIsImageModalOpen(false);
    },
    [editor]
  );
  // #endregion

  // #region renderers
  const renderElement = (props: CustomRenderElementProps) => {
    switch (props.element.type) {
      case "block-quote":
        return <BlockQuoteElement {...props} />;
      case "code-block":
        return <CodeBlockElement {...props} />;
      case "heading":
        switch (props.element.level) {
          case 1:
            return (
              <Header1Element
                isOrdered={Boolean(props.element.isOrdered)}
                {...props}
              />
            );
          case 2:
            return <Header2Element {...props} />;
          case 3:
            return <Header3Element {...props} />;
        }
      case "unordered-list":
        return <UnorderedListElement {...props} />;
      case "ordered-list":
        return <OrderedListElement {...props} />;
      case "list-item":
        return <ListItemElement {...props} />;
      case "link":
        return (
          <LinkElement {...props} element={props.element as LinkElementType} />
        );
      case "hr":
        return <HorizontalLineElement {...props} />;
      case "image":
        return (
          <ImageElement
            {...props}
            element={props.element as ImageElementType}
          />
        );
      default:
        return <DefaultElement {...props} />;
    }
  };

  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <Leaf {...props} />,
    []
  );
  // #endregion

  useEffect(() => {
    editor.children = value || initialValue;
    if (readOnly) {
      editor.onChange();
    }
  }, [value, initialValue, editor, readOnly]);

  const isModalOpen = useMemo(() => isImageModalOpen, [isImageModalOpen]);

  return (
    <div className={`q-editor-component ${customClassName}`}>
      <Slate editor={editor} initialValue={initialValue} onChange={setValue}>
        {layout === "read-only" ? (
          <>
            <div className="q-editor-container">
              <div className="q-editor-content">
                {children}
                <Editable
                  renderElement={renderElement}
                  renderLeaf={renderLeaf}
                  className="q-editor-editable q-editor-editable--read-only"
                  readOnly={readOnly}
                  autoFocus
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <Toolbar
              displayAd={() => setIsAdVisible(!isAdVisible)}
              isAdVisible={isAdVisible}
              isImageModalOpen={isImageModalOpen}
              toggleImageModal={setIsImageModalOpen}
              canUploadImages={!!imageRole}
            />
            <div
              className={`q-editor-container ${
                isModalOpen ? "modal-open" : ""
              }`}
            >
              <ImagePrompt
                isOpen={isImageModalOpen}
                toggleOpen={setIsImageModalOpen}
                onUpload={handleOnUpload}
                imageRole={imageRole}
                uploadUrl={uploadUrl}
                accessToken={accessToken}
              />
              <div className="q-editor-content">
                <div
                  className="ad-block"
                  style={{ display: isAdVisible ? "block" : "none" }}
                >
                  AD
                </div>
                <Editable
                  renderElement={renderElement}
                  renderLeaf={renderLeaf}
                  onKeyDown={handleOnKeyDown}
                  className="q-editor-editable"
                  readOnly={readOnly}
                />
              </div>
            </div>
          </>
        )}
      </Slate>
    </div>
  );
};
